import { IGameState } from "State/Game/IGameState";
import Element from "../Element";
import { SeasonPassRank } from "Layouts/Quests/SeasonPass/SeasonPassRank";
import SpriteComponent from "Component/Render/Sprite";
import TextComponent from "Component/Render/Text";
import Renderable from "Component/Renderable";
import { getSeasonPassRewardForRank } from "State/Player/PlayerSeasonPassHelper";
import { getCurrentTractorPosition } from "System/UI/SeasonPass/TractorHelper";
import TransformComponent from "Component/Transform";
import { getRewardData } from "Utility/RewardHelper";
import GameObject, { GameObjectEvent } from "GameObject/GameObject";
import Interactable from "Component/Interactable";
import { AnimationClipEvent } from "System/Animation/AnimationClip";
import { StartAnimationClip } from "System/Animation/AnimationHelper";
import { Alpha, DragonbonesFadeIn } from "System/Animation/Clip";
import Game from "Game";
import { showPurchasePopup } from "System/UI/Menu/SeasonPassMenuHelper";
import QuestsMenuHandler from "System/UI/Menu/QuestsMenuHandler";
import { EventQuestPopupLayout } from "Constants/Events/CurrentEvent";
import { GLayout } from "Utility/layout/LayoutHelper";
import Armature from "Prototype/Armature";
import { Point } from "pixi.js";
import { SEASON_PASS_INTRO_INFO } from "Constants/SeasonPass";
import { hasFlag } from "State/Player/PlayerFlagHelper";
import { PlaySound } from "System/Audio/AudioHelper";

export const PROGRESS_BAR_MAX_WIDTH = 180;

type SeasonPassRankItemProps = {
    state: IGameState;
    key: number;
    itemWidth: number;
    questsMenuHandler: QuestsMenuHandler;
};

const AnimationNames = {
    RewardGlow: 'rewardglow_ani',
    RewardGlowIdle: 'rewardglow_idle',
    RewardGlowPreview: 'rewardglow_ani_preview',
}

export class SeasonPassRankItem extends Element {
    private state: IGameState;
    private layout: any;
    private key: number;
    private questsMenuHandler: QuestsMenuHandler;
    private freeRewardArmature: Armature;
    private premiumRewardArmature: Armature;
    private freeRewardCheckArmature: Armature;
    private premiumRewardCheckArmature: Armature;
    private rewardLockArmature: Armature;

    public create(props: SeasonPassRankItemProps): void {
        const x = props.key * props.itemWidth;

        super.create({
            type: 'group',
            layout: {
                anchors: [0, 0, 0, 0],
                pivot: [0, 0],
                offsets: [-x, 0, -x, 0],
            },
        });

        this.state = props.state;
        this.key = props.key;
        this.questsMenuHandler = props.questsMenuHandler;
        this.layout = new SeasonPassRank();
        this.addChild(this.layout.root);

        this.freeRewardArmature = GameObject.Instantiate(Armature, {
            dragonbonesName: 'filling_progress_bar',
            armatureName: 'reward_reached',
            alpha: 0,
            playOnStart: false,
            position: new Point(56, -70),
        });
        this.layout.FreeReward.RewardGlowContainer.addChild(this.freeRewardArmature);

        this.premiumRewardArmature = GameObject.Instantiate(Armature, {
            dragonbonesName: 'filling_progress_bar',
            armatureName: 'reward_reached',
            alpha: 0,
            playOnStart: false,
            position: new Point(56, -70),
        });
        this.layout.PremiumReward.RewardGlowContainer.addChild(this.premiumRewardArmature);

        this.freeRewardCheckArmature = GameObject.Instantiate(Armature, {
            dragonbonesName: 'filling_progress_bar',
            armatureName: 'check_mark',
            alpha: 0,
            playOnStart: false,
            position: new Point(56, 140),
        });
        this.layout.FreeReward.RewardGlowContainer.addChild(this.freeRewardCheckArmature);

        this.premiumRewardCheckArmature = GameObject.Instantiate(Armature, {
            dragonbonesName: 'filling_progress_bar',
            armatureName: 'check_mark',
            alpha: 0,
            playOnStart: false,
            position: new Point(56, 140),
        });
        this.layout.PremiumReward.RewardGlowContainer.addChild(this.premiumRewardCheckArmature);

        this.rewardLockArmature = GameObject.Instantiate(Armature, {
            dragonbonesName: 'lock_removed',
            armatureName: 'Armature',
            alpha: 0,
            playOnStart: false,
            position: new Point(-1160, 150),
        });
        this.layout.PremiumReward.RewardLock.addChild(this.rewardLockArmature);

        this.init();
    }

    public init(): void {
        const freeReward = getSeasonPassRewardForRank(this.state, this.key + 1, 'free')?.rewards[0];
        const premiumReward = getSeasonPassRewardForRank(this.state, this.key + 1, 'premium')?.rewards[0];

        const freeRewardData = getRewardData(freeReward.type);
        const premiumRewardData = getRewardData(premiumReward.type);
        this.layout.FreeReward.RewardIcon.getComponent(SpriteComponent).texture = freeRewardData.texture;
        this.layout.FreeReward.RewardText.getComponent(TextComponent).text = `x${freeReward.amount}`;
        this.layout.FreeReward.RewardIcon.getComponent(TransformComponent).scale.set((freeRewardData.iconScale ?? 1) - 0.35);

        this.layout.PremiumReward.RewardIcon.getComponent(SpriteComponent).texture = premiumRewardData.texture;
        this.layout.PremiumReward.RewardText.getComponent(TextComponent).text = `x${premiumReward.amount}`;
        this.layout.PremiumReward.RewardIcon.getComponent(TransformComponent).scale.set((premiumRewardData.iconScale ?? 1) - 0.35);

        if (this.state.playerState.seasonPass.boughtPremiumPass) {
            this.layout.PremiumReward.RewardLock.getComponent(Renderable).visible = false;
        }

        if (getCurrentTractorPosition(this.state.playerState, true).rank > this.key) {
            if (hasFlag(Game.store.getState().playerState, SEASON_PASS_INTRO_INFO)) {
                StartAnimationClip(
                    new DragonbonesFadeIn(this.freeRewardArmature, {
                        animationName: AnimationNames.RewardGlow,
                        playTimes: 1,
                        progress: 1
                    })
                );
                StartAnimationClip(
                    new DragonbonesFadeIn(this.freeRewardCheckArmature, {
                        animationName: 'check_mark',
                        playTimes: 1,
                        progress: 1
                    })
                );
            }
            if (this.state.playerState.seasonPass.boughtPremiumPass) {
                StartAnimationClip(
                    new DragonbonesFadeIn(this.premiumRewardArmature, {
                        animationName: AnimationNames.RewardGlow,
                        playTimes: 1,
                        progress: 1
                    })
                );
                StartAnimationClip(
                    new DragonbonesFadeIn(this.premiumRewardCheckArmature, {
                        animationName: 'check_mark',
                        playTimes: 1,
                        progress: 1
                    })
                );
            } else {
                StartAnimationClip(
                    new DragonbonesFadeIn(this.premiumRewardArmature, {
                        animationName: AnimationNames.RewardGlowIdle,
                        playTimes: 1,
                        progress: 1
                    })
                );
            }
        } else {
            this.freeRewardCheckArmature.getComponent(Renderable).alpha = 0;
            this.premiumRewardCheckArmature.getComponent(Renderable).alpha = 0;
            this.freeRewardArmature.getComponent(Renderable).alpha = 0;
            this.premiumRewardArmature.getComponent(Renderable).alpha = 0;
        }

        this.handleInteractions();
    }

    public updateLayout(state: IGameState) {
        this.state = state;
        this.init();
    }

    public destroy() {
        super.destroy();
    }

    public updateState(state: IGameState) {
        this.state = state;
        this.handleInteractions();
    }

    private showTooltip(gameObject: GameObject): void {
        this.fadeIn(gameObject);

        Game.ticker.setTimeout(() => {
            this.fadeOut(gameObject);
        }, 2000);
    }

    private fadeIn(gameObject: GameObject, endAlpha: number = 1) {
        StartAnimationClip(
            new Alpha(gameObject, {
                startAlpha: 0,
                endAlpha,
                duration: 450,
            })
        );
    }

    private fadeOut(gameObject: GameObject, startAlpha: number = 1) {
        StartAnimationClip(
            new Alpha(gameObject, {
                startAlpha,
                endAlpha: 0,
                duration: 450,
            }).once(AnimationClipEvent.DESTROY, () => {
                gameObject.getComponent(Renderable).visible = false;
            })
        );
    }

    private handleInteractions() {
        if (getCurrentTractorPosition(this.state.playerState, true).rank > this.key) {
            const premiumRewardContainerInteractable = this.layout.PremiumReward.RewardContainer.getComponent(Interactable);
            if (premiumRewardContainerInteractable) {
                premiumRewardContainerInteractable.enabled = false;
                premiumRewardContainerInteractable.buttonMode = false;
            }

            if (!this.state.playerState.seasonPass.boughtPremiumPass) {
                this.layout.PremiumReward.RewardLock.addComponent(Interactable);
                this.layout.PremiumReward.RewardLock.getComponent(Interactable).buttonMode = true;
                this.layout.PremiumReward.RewardLock.on(GameObjectEvent.SELECTED, () => {
                    showPurchasePopup(this.questsMenuHandler.layout as GLayout<EventQuestPopupLayout>, this.questsMenuHandler.seasonPassRankLayout);
                });
            }

            const freeRewardContainerInteractable = this.layout.FreeReward.RewardContainer.getComponent(Interactable);
            if (freeRewardContainerInteractable) {
                freeRewardContainerInteractable.enabled = false;
                freeRewardContainerInteractable.buttonMode = false;
            }
        } else {
            this.layout.FreeReward.RewardContainer.addComponent(Interactable);
            this.layout.FreeReward.RewardContainer.getComponent(Interactable).buttonMode = true;
            this.layout.FreeReward.RewardContainer.on(GameObjectEvent.SELECTED, () => {
                this.showTooltip(this.layout.FreeReward.RewardTooltip);
            });

            this.layout.PremiumReward.RewardContainer.addComponent(Interactable);
            this.layout.PremiumReward.RewardContainer.getComponent(Interactable).buttonMode = true;
            this.layout.PremiumReward.RewardContainer.on(GameObjectEvent.SELECTED, () => {
                this.showTooltip(this.layout.PremiumReward.RewardTooltip);
            });
        }
    }

    public animationRewardUnlocked(delay: number = 0) {
        const freeRewardDelay = this.state.playerState.seasonPass.boughtPremiumPass ? 500 : 100;
        if (this.state.playerState.seasonPass.boughtPremiumPass) {
            setTimeout(() => {
                PlaySound('chest-upgrade-2');
            }, delay);
            StartAnimationClip(
                new DragonbonesFadeIn(this.premiumRewardArmature, {
                    animationName: AnimationNames.RewardGlow,
                    playTimes: 1,
                    delay
                })
            );
            StartAnimationClip(
                new DragonbonesFadeIn(this.premiumRewardCheckArmature, {
                    animationName: 'check_mark',
                    playTimes: 1,
                    delay
                })
            );
        } else {
            StartAnimationClip(
                new DragonbonesFadeIn(this.premiumRewardArmature, {
                    animationName: AnimationNames.RewardGlowIdle,
                    playTimes: 1,
                    delay
                })
            );
        }
        setTimeout(() => {
            PlaySound('chest-upgrade-1');
        }, delay + freeRewardDelay);
        StartAnimationClip(
            new DragonbonesFadeIn(this.freeRewardArmature, {
                animationName: AnimationNames.RewardGlow,
                playTimes: 1,
                delay: freeRewardDelay + delay
            })
        );
        StartAnimationClip(
            new DragonbonesFadeIn(this.freeRewardCheckArmature, {
                animationName: 'check_mark',
                playTimes: 1,
                delay: freeRewardDelay + delay,
                fadeInTime: 200
            })
        );
    }

    public unlockPremiumReward() {
        this.layout.PremiumReward.RewardLockIcon.getComponent(Renderable).alpha = 0;
        StartAnimationClip(
            new DragonbonesFadeIn(this.rewardLockArmature, {
                animationName: 'lock_unlocked',
                playTimes: 1,
            }).once(AnimationClipEvent.COMPLETE, () => {
                this.layout.PremiumReward.RewardLock.getComponent(Renderable).visible = false;
            })
        );
    }

    public claimPremiumReward(delay: number, onComplete: () => void) {
        setTimeout(() => {
            PlaySound('chest-upgrade-2');
        }, delay);
        StartAnimationClip(
            new DragonbonesFadeIn(this.premiumRewardArmature, {
                animationName: AnimationNames.RewardGlow,
                playTimes: 1,
                delay
            })
        );
        StartAnimationClip(
            new DragonbonesFadeIn(this.premiumRewardCheckArmature, {
                animationName: 'check_mark',
                playTimes: 1,
                delay
            }).once(AnimationClipEvent.COMPLETE, onComplete)
        );
    }
}
